import React, { useState, useEffect } from "react";
import { ArrowRight, Search, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext";
import api from "../../lib/api.ts";
import fallbackImage from "../../assets/bg-dashboard.png";
import { useRef } from "react";

// ─── Custom Select Component ────────────────────────────────────────────────
const CustomSelect = ({
  label,
  value,
  options,
  onChange,
  placeholder,
  displayKey = "name",
  valueKey = "id",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getDisplayText = () => {
    if (!value) return placeholder || "All";
    const selected = options.find(
      (opt) => String(opt[valueKey]) === String(value),
    );
    return selected ? selected[displayKey] || selected.name || "All" : "All";
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="bg-[#1c1c1e]/90 rounded-xl p-4 border border-white/10 cursor-pointer hover:border-blue-500/40 transition-all group backdrop-blur-sm"
      >
        <label className="text-gray-400 text-[10px] font-black uppercase tracking-wider mb-1 block">
          {label}
        </label>
        <div className="flex items-center justify-between">
          <span className="text-white text-sm font-semibold truncate pr-2">
            {getDisplayText()}
          </span>
          <ChevronDown
            className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
          />
        </div>
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-[#111113]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl max-h-64 overflow-y-auto z-50">
          {options.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500 italic">
              No options available
            </div>
          ) : (
            options.map((opt, idx) => (
              <div
                key={idx}
                onClick={() => {
                  onChange(opt[valueKey] || opt.id);
                  setIsOpen(false);
                }}
                className="px-4 py-3 text-sm text-gray-300 hover:bg-primary/30 hover:text-white cursor-pointer transition-colors"
              >
                {opt[displayKey] || opt.name || "Unnamed"}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export function Hero() {
  const navigate = useNavigate();
  const { language, translate } = useLanguage();
  const isRTL = language === "ar";

  // ─── States ────────────────────────────────────────────────────────────────
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slides, setSlides] = useState([]);
  const [stats, setStats] = useState([]);
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchState, setSearchState] = useState({
    make: "",
    model: "",
    variant: "",
    year: "",
    query: "",
  });

  // ─── Fetch Hero + Stats + Makes ────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // 1. Fetch homepage settings
        const res = await api.get(`/pagesettings/homepage?lang=${language}`);
        const data = res.data || {};

        // 2. Process hero_section slides (multiple supported)
        const rawHero = data.hero_section || [];
        const heroItems = Array.isArray(rawHero)
          ? rawHero
          : [rawHero].filter(Boolean);

        const processedSlides = heroItems.map((item) => ({
          title: item?.title?.value || "The Future of Luxury Vehicle Auctions",
          subtitle: item?.subtitle?.value || "",
          primaryCtaText: item?.btnText?.value || "View Auctions",
          primaryCtaUrl: item?.btnLink?.value || "/vehicles",
          secondaryCtaText: item?.secondaryBtnText?.value || "",
          secondaryCtaUrl: item?.secondaryBtnLink?.value || "",
          bgimg: item?.imageUrl?.value
            ? item.imageUrl.value.startsWith("http")
              ? item.imageUrl.value
              : `${process.env.REACT_APP_API_URL || ""}${item.imageUrl.value.startsWith("/") ? "" : "/"}${item.imageUrl.value}`
            : fallbackImage,
        }));

        setSlides(
          processedSlides.length
            ? processedSlides
            : [
                {
                  title:
                    "The Future of <span class='text-primary'>Luxury</span> Vehicle Auctions",
                  subtitle:
                    "Experience the most secure, transparent, and premium trading platform...",
                  primaryCtaText: "View Auctions",
                  primaryCtaUrl: "/vehicles",
                  secondaryCtaText: "",
                  secondaryCtaUrl: "",
                  bgimg: fallbackImage,
                },
              ],
        );

        // 3. Process hero_stats
        const rawStats = data.hero_stats || [];
        const processedStats = Array.isArray(rawStats)
          ? rawStats.map((s) => ({
              number: s?.number?.value || "0",
              label: s?.label?.value || "Stat",
            }))
          : [];

        setStats(
          processedStats.length
            ? processedStats
            : [
                { number: "1.2k+", label: "Active Listings" },
                { number: "$450M+", label: "Total Volume" },
                { number: "99.9%", label: "Secure Trades" },
              ],
        );

        // 4. Fetch makes
        const makesRes = await api.get(`/makes`);
        setMakes(makesRes.data || []);
      } catch (err) {
        console.error("Hero data fetch error:", err);
        // Fallbacks already set in state initialization
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [language]);

  // ─── Fetch Models when make changes ────────────────────────────────────────
  useEffect(() => {
    if (!searchState.make) {
      setModels([]);
      setVariants([]);
      setSearchState((prev) => ({ ...prev, model: "", variant: "" }));
      return;
    }

    const fetchModels = async () => {
      try {
        const res = await api.get(`/models?makeId=${searchState.make}`);
        setModels(res.data || []);
      } catch (e) {
        console.error("Models fetch error:", e);
      }
    };

    fetchModels();
  }, [searchState.make]);

  // ─── Fetch Variants when model changes ─────────────────────────────────────
  useEffect(() => {
    if (!searchState.model) {
      setVariants([]);
      setSearchState((prev) => ({ ...prev, variant: "" }));
      return;
    }

    const fetchVariants = async () => {
      try {
        const res = await api.get(`/variants?modelId=${searchState.model}`);
        setVariants(res.data || []);
      } catch (e) {
        console.error("Variants fetch error:", e);
      }
    };

    fetchVariants();
  }, [searchState.model]);

  // ─── Auto-slide ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (slides.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  // ─── Handlers ──────────────────────────────────────────────────────────────
  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchState.make) params.append("makeId", searchState.make);
    if (searchState.model) params.append("modelId", searchState.model);
    if (searchState.variant) params.append("variantId", searchState.variant);
    if (searchState.year) params.append("year", searchState.year);
    if (searchState.query) params.append("search", searchState.query);
    navigate(`/vehicles?${params.toString()}`);
  };

  const highlightTitle = (title) => {
    if (!title) return "";
    return title.replace(
      /(Luxury Vehicle)/gi,
      '<br /><span class="text-[#135EA0]">$1<br/></span>',
    );
  };

  if (loading) {
    return (
      <div className="h-screen bg-bg-dark flex items-center justify-center text-white font-bold tracking-widest">
        LOADING...
      </div>
    );
  }

  const activeSlide = slides[currentSlide] || slides[0];

  return (
    <section
      className="relative flex items-center overflow-hidden bg-bg-light"
      dir={isRTL ? "rtl" : "ltr"}
    >
      {/* Background Slides */}
      {/* {slides.map((slide, idx) => (
        <div
          key={idx}
          className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
            idx === currentSlide ? "opacity-70" : "opacity-0"
          }`}
          style={{
            backgroundImage: `url(${slide.bgimg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
      ))} */}

      {/* Overlays */}
      {/* <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-transparent to-black/90 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/60 pointer-events-none" /> */}

      <div
        //  className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full pt-32 pb-24"
        className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full py-16"
      >
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* ─── Left Side ──────────────────────────────────────────────── */}
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 sm:gap-3 bg-primary/10 border border-primary/40 px-4 sm:px-6 py-2 sm:py-2.5 rounded-full whitespace-nowrap">
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-primary animate-pulse shrink-0" />

              <span className="text-primary text-[12px] sm:text-xs font-bold uppercase tracking-[0.15em] sm:tracking-[0.25em]">
                NEXT GENERATION MARKETPLACE
              </span>
            </div>
            <h1
              className="text-6xl md:text-6xl lg:text-6xl font-black leading-[1.1] tracking-tighter text-white"
              dangerouslySetInnerHTML={{
                __html: highlightTitle(activeSlide.title),
              }}
            />

            <p
              className="text-gray-300 text-base md:text-xl max-w-2xl leading-relaxed"
              dangerouslySetInnerHTML={{ __html: activeSlide.subtitle }}
            />

            <div className="flex flex-col sm:flex-row gap-6 pt-6">
              {/* Primary CTA */}
              <button
                onClick={() =>
                  navigate(activeSlide.primaryCtaUrl || "/vehicles")
                }
                className="bg-primary hover:bg-primary-hover text-white px-8 py-4 rounded font-black text-base flex items-center justify-center gap-3 transition-all shadow-xl shadow-blue-900/40 active:scale-95"
              >
                {activeSlide.primaryCtaText || "View uctions"}
                <ArrowRight
                  className={`w-6 h-6 ${isRTL ? "rotate-180" : ""}`}
                />
              </button>

              {/* Secondary CTA – only show if at least text or url exists */}
              {(activeSlide.secondaryCtaText ||
                activeSlide.secondaryCtaUrl) && (
                <button
                  onClick={() => navigate(activeSlide.secondaryCtaUrl || "#")}
                  className="bg-transparent border-2 border-white/40 hover:border-white/70 text-white px-8 py-4 rounded font-black text-base transition-all active:scale-95"
                >
                  {activeSlide.secondaryCtaText || "Learn More"}
                </button>
              )}
            </div>

            {/* ─── Dynamic Stats ──────────────────────────────────────── */}
            <div
              // className="grid grid-cols-3 gap-8 pt-16 border-t border-white/10"
              className="grid grid-cols-3 gap-6 pt-8 border-t border-white/10"
            >
              {stats.map((stat, idx) => (
                <div key={idx} className="text-center">
                  <p className="text-lg font-black text-white">{stat.number}</p>
                  <p className="text-gray-400 text-sm md:text-base mt-2 uppercase tracking-wider font-medium">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* ─── Right Side - Search Card ──────────────────────────────── */}
          {/* <div className="relative group">
            <div className="bg-[#0f0f11]/95 backdrop-blur-2xl border-l-4 border-blue-600 border-t border-r border-b border-white/5 rounded-3xl p-8 lg:p-10 shadow-2xl shadow-black/70">
              <h3 className="text-white font-black text-2xl tracking-widest mb-10 uppercase flex items-center gap-4">
                <Search className="w-6 h-6 text-blue-500" />
                ADVANCED SEARCH
              </h3>

              <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <CustomSelect
                    label="MAKE"
                    value={searchState.make}
                    options={makes}
                    placeholder="All Brands"
                    onChange={(val) => setSearchState((prev) => ({ ...prev, make: val, model: '', variant: '' }))}
                  />

                  <CustomSelect
                    label="MODEL"
                    value={searchState.model}
                    options={models}
                    placeholder="All Models"
                    onChange={(val) => setSearchState((prev) => ({ ...prev, model: val, variant: '' }))}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <CustomSelect
                    label="TRIM"
                    value={searchState.variant}
                    options={variants}
                    placeholder="All trims"
                    onChange={(val) => setSearchState((prev) => ({ ...prev, variant: val }))}
                  />

                  <CustomSelect
                    label="PRODUCTION YEAR"
                    value={searchState.year}
                    options={Array.from({ length: 31 }, (_, i) => {
                      const year = String(new Date().getFullYear() - i);
                      return { id: year, name: year };
                    })}
                    placeholder="Select year"
                    displayKey="name"
                    valueKey="id"
                    onChange={(val) => setSearchState((prev) => ({ ...prev, year: val }))}
                  />
                </div>

                <div className="bg-[#1a1a1d]/80 rounded-2xl p-5 border border-white/5 focus-within:border-blue-600/40 transition-all">
                  <label className="text-gray-500 text-xs font-black uppercase tracking-widest mb-2 block">
                    SEARCH QUERY
                  </label>
                  <input
                    type="text"
                    value={searchState.query}
                    onChange={(e) => setSearchState((prev) => ({ ...prev, query: e.target.value }))}
                    placeholder="Model, VIN or Reference #"
                    className="w-full bg-transparent text-white text-base font-semibold placeholder-gray-500 focus:outline-none"
                  />
                </div>

                <button
                  onClick={handleSearch}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white py-5 rounded-2xl font-black text-lg flex items-center justify-center gap-3 transition-all shadow-xl shadow-blue-900/40 active:scale-[0.98]"
                >
                  <Search className="w-5 h-5" />
                  EXECUTE SEARCH
                </button>
              </div>
            </div>
          </div> */}
          <div className="relative group">
            <div className="bg-[#0f0f11]/95 backdrop-blur-2xl  border-primary border border-white/5 rounded-3xl p-5 lg:p-6 shadow-2xl shadow-black/60">
              <h3 className="text-white font-bold text-lg tracking-wider pt-2 mb-4 uppercase flex items-center gap-3">
                <Search className="w-5 h-5 text-primary" />
                ADVANCED SEARCH
              </h3>

              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <CustomSelect
                    label="MAKE"
                    value={searchState.make}
                    options={makes}
                    placeholder="All Brands"
                    onChange={(val) =>
                      setSearchState((prev) => ({
                        ...prev,
                        make: val,
                        model: "",
                        variant: "",
                      }))
                    }
                  />

                  <CustomSelect
                    label="MODEL"
                    value={searchState.model}
                    options={models}
                    placeholder="All Models"
                    onChange={(val) =>
                      setSearchState((prev) => ({
                        ...prev,
                        model: val,
                        variant: "",
                      }))
                    }
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <CustomSelect
                    label="TRIM"
                    value={searchState.variant}
                    options={variants}
                    placeholder="All trims"
                    onChange={(val) =>
                      setSearchState((prev) => ({ ...prev, variant: val }))
                    }
                  />

                  <CustomSelect
                    label="PRODUCTION YEAR"
                    value={searchState.year}
                    options={Array.from({ length: 31 }, (_, i) => {
                      const year = String(new Date().getFullYear() - i);
                      return { id: year, name: year };
                    })}
                    placeholder="Select year"
                    displayKey="name"
                    valueKey="id"
                    onChange={(val) =>
                      setSearchState((prev) => ({ ...prev, year: val }))
                    }
                  />
                </div>

                <div className="bg-[#1a1a1d]/80 rounded-xl p-3 border border-white/5 focus-within:border-blue-600/40 transition-all">
                  <label className="text-gray-500 text-[10px] font-bold uppercase tracking-wider mb-1 block">
                    SEARCH QUERY
                  </label>
                  <input
                    type="text"
                    value={searchState.query}
                    onChange={(e) =>
                      setSearchState((prev) => ({
                        ...prev,
                        query: e.target.value,
                      }))
                    }
                    placeholder="Model, VIN or Ref #"
                    className="w-full bg-transparent text-white text-sm font-medium placeholder-gray-500 focus:outline-none"
                  />
                </div>
 
              </div>
              <div className="mb-2 mt-6"> 
                <button
                  onClick={handleSearch}
                  className="w-full bg-gradient-to-r from-primary to-primary-dark hover:from-primary-hover hover:to-primary-darker text-white py-3.5 rounded font-bold text-sm flex items-center justify-center gap-2 transition-all shadow-lg shadow-blue-900/30 active:scale-[0.98]"
                >
                  <Search className="w-4 h-4" />
                  EXECUTE SEARCH
                </button>
                </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
